import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { Popover, Transition } from '@headlessui/react';
import { Button } from './Button';
import { Container } from './Container';
import { Logo } from './Logo';
import { NavLink } from './NavLink';
import { useSignupModal } from './SignupModalContext';

function MobileNavLink({ href, children }) {
  const isHash = typeof href === 'string' && href.startsWith('#');
  const className = 'block w-full p-2 text-slate-700 hover:text-slate-950 transition-colors text-left';
  if (isHash) {
    return (
      <Popover.Button as="a" href={href} className={className}>
        {children}
      </Popover.Button>
    );
  }
  return (
    <Popover.Button as={Link} to={href} className={className}>
      {children}
    </Popover.Button>
  );
}

function MobileNavCta({ onClick, children }) {
  return (
    <Popover.Button
      as="button"
      type="button"
      onClick={onClick}
      className="mt-2 flex h-11 w-full items-center justify-center rounded-xl border border-gch-accent/55 bg-gch-accent px-4 text-sm font-bold text-white shadow-lg shadow-gch-accent/20 transition-colors hover:bg-gch-accent/90"
    >
      {children}
    </Popover.Button>
  );
}

function MobileNavIcon({ open }) {
  return (
    <svg aria-hidden="true" className="h-3.5 w-3.5 overflow-visible stroke-slate-700" fill="none" strokeWidth={2} strokeLinecap="round">
      <path d="M0 1H14M0 7H14M0 13H14" className={`origin-center transition ${open ? 'scale-90 opacity-0' : ''}`} />
      <path d="M2 2L12 12M12 2L2 12" className={`origin-center transition ${!open ? 'scale-90 opacity-0' : ''}`} />
    </svg>
  );
}

function MobileNavigation({ onSignupClick }) {
  return (
    <Popover>
      <Popover.Button className="relative z-10 flex h-11 w-11 items-center justify-center rounded-lg ui-not-focus-visible:outline-none" aria-label="Toggle Navigation">
        {({ open }) => <MobileNavIcon open={open} />}
      </Popover.Button>
      <Transition.Root>
        <Transition.Child as={Fragment} enter="duration-150 ease-out" enterFrom="opacity-0" enterTo="opacity-100" leave="duration-150 ease-in" leaveFrom="opacity-100" leaveTo="opacity-0">
          <Popover.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
        </Transition.Child>
        <Transition.Child as={Fragment} enter="duration-150 ease-out" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="duration-100 ease-in" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
          <Popover.Panel as="div" className="absolute inset-x-0 top-full mt-4 flex origin-top flex-col rounded-xl bg-white p-4 text-lg tracking-tight text-slate-700 shadow-xl ring-1 ring-slate-900/10">
            <MobileNavLink href="#channels">Channels</MobileNavLink>
            <MobileNavLink href="#games">Games</MobileNavLink>
            <MobileNavLink href="#pricing">Pricing</MobileNavLink>
            <MobileNavLink href="#faq">FAQ</MobileNavLink>
            <hr className="m-2 border-white/10" />
            <MobileNavLink href="/studio/login">Log in</MobileNavLink>
            <MobileNavCta onClick={onSignupClick}>Build a game free</MobileNavCta>
          </Popover.Panel>
        </Transition.Child>
      </Transition.Root>
    </Popover>
  );
}

export function Header() {
  const { openSignupModal } = useSignupModal();
  return (
    <header className="sf-landing-header fixed left-0 right-0 top-0 z-50 border-b border-white/45 bg-white/65 py-3 backdrop-blur-xl">
      <Container>
        <nav className="relative z-50 flex justify-between">
          <div className="sf-header-primary flex items-center">
            <Link to="/" aria-label="Home">
              <Logo className="h-10 w-auto" />
            </Link>
            <div className="sf-header-main-nav">
              <NavLink href="#channels">Channels</NavLink>
              <NavLink href="#games">Games</NavLink>
              <NavLink href="#pricing">Pricing</NavLink>
              <NavLink href="#faq">FAQ</NavLink>
            </div>
          </div>
          <div className="sf-header-actions flex items-center gap-x-4">
            <Link to="/studio/login" className="sf-header-login text-sm font-medium text-slate-700 hover:text-slate-950 transition-colors">
              Log in
            </Link>
            <Button onClick={openSignupModal} className="sf-header-cta h-9 rounded-[12px] border-gch-accent/55 px-4 py-2 text-xs">
              Build a game free
            </Button>
            <div className="sf-header-mobile -mr-1">
              <MobileNavigation onSignupClick={openSignupModal} />
            </div>
          </div>
        </nav>
      </Container>
    </header>
  );
}
